(() => {
  const IMG_SWAP_DELAY = 200;
  const IMG_CLEAR_DELAY = 400;
  const DEBOUNCE_DELAY = 200;

  const handleize = (str) =>
    String(str ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const formatMoney = (cents) =>
    typeof theme !== 'undefined' && theme.Currency && theme.shopSettings
      ? theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat)
      : `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;

  const getVariantFilterOpts = () => {
    const opts = {};
    new URLSearchParams(window.location.search).forEach((val, key) => {
      const match = key.match(/^filter\.v\.option\.(.+)$/);
      if (match) opts[match[1]] = val;
    });
    return opts;
  };

  const hasAnyActiveFilter = () => {
    for (const key of new URLSearchParams(window.location.search).keys()) {
      if (key.startsWith('filter.v.option.')) return true;
    }
    return false;
  };

  const findFilteredVariant = (variants, options, filterOpts) => {
    const filterKeys = Object.keys(filterOpts);
    if (!filterKeys.length) return null;

    return variants.find((variant) => {
      if (!variant.available) return false;
      return filterKeys.every((optHandle) => {
        const filterVal = filterOpts[optHandle];
        const matchedByName = options.some(
          (opt, i) => handleize(opt.name) === optHandle && variant[`option${i + 1}`] === filterVal
        );
        return matchedByName;
      });
    }) ?? null;
  };

  const applyVariantToCard = (card, variant) => {
    const baseUrl = card.dataset.baseUrl ?? card.dataset.productUrl?.split('?')[0] ?? '';
    const variantUrl = `${baseUrl}?variant=${variant.id}`;

    if (variant.featured_image?.src) {
      const img = card.querySelector('.product-card__img');
      if (img) {
        const src = variant.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, (m, ext, qs) =>
          ext + (qs ? qs + '&width=600' : '?width=600'));
        img.classList.add('is-fading');
        setTimeout(() => {
          // Drop the responsive srcset, otherwise the browser keeps showing the srcset image.
          img.removeAttribute('srcset');
          img.src = src;
          img.onload = () => img.classList.remove('is-fading');
          setTimeout(() => img.classList.remove('is-fading'), IMG_CLEAR_DELAY);
        }, IMG_SWAP_DELAY);
      }
    }

    const priceEl = card.querySelector('.product-card__price');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);

    const compareEl = card.querySelector('.product-card__compare-price');
    if (compareEl) {
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEl.textContent = hasDiscount ? formatMoney(variant.compare_at_price) : '';
      compareEl.style.display = hasDiscount ? '' : 'none';
    }

    card.dataset.productUrl = variantUrl;
    card.querySelectorAll('a').forEach((anchor) => {
      const href = anchor.getAttribute('href') ?? '';
      if (href === baseUrl || href.startsWith(`${baseUrl}?`)) anchor.href = variantUrl;
    });
  };

  const syncCard = (card, hasFilter, filterOpts) => {
    const variantsEl = card.querySelector('.pcd-variants-json');
    const optionsEl = card.querySelector('.pcd-options-json');
    if (!variantsEl || !optionsEl) return;

    let variants, options;
    try {
      variants = JSON.parse(variantsEl.textContent);
      options = JSON.parse(optionsEl.textContent);
    } catch { return; }

    if (hasFilter) {
      const matched = findFilteredVariant(variants, options, filterOpts);
      if (matched) applyVariantToCard(card, matched);
      return;
    }

    // Skip restore if card was server-rendered with an active filter (race guard)
    if (card.dataset.variantFilterActive === 'true') return;

    const fvId = parseInt(card.dataset.fvId, 10);
    const fbId = parseInt(card.dataset.fbId, 10);
    const target =
      (fvId && variants.find((v) => v.id === fvId && v.available)) ||
      (fbId && variants.find((v) => v.id === fbId && v.available)) ||
      null;

    if (target) applyVariantToCard(card, target);
  };

  const sync = () => {
    const filterOpts = getVariantFilterOpts();
    const hasFilter = hasAnyActiveFilter();
    document.querySelectorAll('.product-card[data-product-id]').forEach((card) => {
      syncCard(card, hasFilter, filterOpts);
    });
  };

  let debounceTimer = null;
  const syncDeferred = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, DEBOUNCE_DELAY);
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('a, button, form, input, .pcd-drawer')) return;
    const card = e.target.closest('.product-card[data-product-url]');
    if (card) window.location.href = card.dataset.productUrl;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }

  document.addEventListener('shopify:section:load', sync);
  window.addEventListener('popstate', sync);

  const patchHistory = (method) => {
    const original = history[method];
    history[method] = function (...args) {
      original.apply(this, args);
      syncDeferred();
    };
  };
  patchHistory('pushState');
  patchHistory('replaceState');

  new MutationObserver((mutations) => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE &&
          (node.classList.contains('product-card') || node.querySelector?.('.product-card'))) {
          syncDeferred();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
